import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, NgZone, OnDestroy, OnInit,
    QueryList, TemplateRef, ViewChild, ViewChildren
} from "@angular/core";
import { ChatHelperService } from "@karma-games/angular-devkit/src/lib/modules/chat";
import { buffer, distinctUntilChanged, merge, pairwise, startWith, Subject, switchMap, takeUntil, timer } from "rxjs";
import gsap from "gsap";
import { IElementDimensions, RulerService } from "@karma-games/angular-devkit/src/lib/services/ruler";
import { filter, map, take } from "rxjs/operators";
import { EScreenOrientation } from "@karma-games/angular-devkit/src/lib/std/globals";
import { IChatOpenedStateData } from "./interfaces";
import { SendBetUseCase } from "../core/round/domain/send-bet.use-case";
import { CancelBetUseCase } from "../core/round/domain/cancel-bet.use-case";
import { IAutoplayOptions, ISendBetArgs, IUserBet } from "../core/round/store/interfaces";
import { Dictionary } from "lodash";
import { CashOutUseCase } from "../core/round/domain/cash-out.use-case";
import {
    EAutoplayStopReason,
    EBetButtonState,
    IAutoplayStopData,
    IBetButtonState,
    IBetPanelData
} from "../bet-panel/interfaces";
import { betButtonStates, betPanelDataDefault } from "../bet-panel/constants";
import { SetAutoplayUseCase } from "../core/round/domain/set-autoplay.use-case";
import { BetPanelsService } from "../bet-panel/bet-panels.service";
import { RoundRepository } from "../core/round/round.repository";
import { GetUserBetsUseCase } from "../core/round/domain/get-user-bets.use-case";
import { GetUserBetByIndexUseCase } from "../core/round/domain/get-user-bet-by-index.use-case";
import { GetRoundStateUseCase } from "../core/round/domain/get-round-state.use-case";
import { GetRoundMultiplierUseCase } from "../core/round/domain/get-round-multiplier.use-case";
import { GetRoundUseCase } from "../core/round/domain/get-round-data.use-case";
import { GetAutoplayUseCase } from "../core/round/domain/get-autoplay-options.use-case";
import { GetBetButtonStateUseCase } from "../bet-panel/domain/get-bet-button-state.use-case";
import { CURRENT_NAMESPACE_TOKEN } from "@karma-games/angular-devkit/src/lib/services/connection-state-handler";
import { ListenForCashOutUseCase } from "../core/round/domain/listen-for-cash-out.use-case";
import { ListenForRoundLosedUseCase } from "../core/round/domain/listen-for-round-losed.use-case";
import { RemoveUserBetUseCase } from "../core/round/domain/remove-user-bet.use-case";
import { GetBetActionInProgressUseCase } from "../bet-panel/domain/get-bet-action-in-progress.use-case";
import { StopAutoplayUseCase } from "../bet-panel/domain/stop-autoplay.use-case";
import {
    AddRemoveUserRoundBetRecordsUseCase
} from "../statistics-panel/domain/update-user-round-bet-records-use-case.service";
import { AddUserBetUseCase } from "../core/round/domain/add-user-bet.use-case";
import { AttachBalanceListenerUseCase } from "../core/user/domain/attach-balance-listener.use-case";
import { GetRoundIdUseCase } from "../core/round/domain/get-round-id.use-case";
import { FetchCurrentRoundStateUseCase } from "../core/round/domain/fetch-current-round-state.use-case";
import { GetRoundInitialStateUseCase } from "../core/round/domain/get-round-initial-state.use-case";
import { LobbyConnectionStateHandlerService } from "./lobby-connection-state-handler.service";
import { GetAutoCashOutUseCase } from "../bet-panel/domain/get-auto-cash-out.use-case";
import { GetAutoplaySendBetUseCase } from "../bet-panel/domain/get-autoplay-send-bet.use-case";
import { EBetSplit, ERoundState, IRoundCashOut } from "../core/round/interfaces";
import { CurrencyService } from "../shared/services/currency.service";
import { BetPanelWalletAnimationDirective } from "../bet-panel/bet-panel-wallet-animation.directive";
import { UpdateUserRoundBetRecordUseCase } from "../statistics-panel/domain/update-user-round-bet-record.use-case";
import { SelectComponent } from "@karma-games/angular-devkit/modules/controls-components";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { runInZone } from "@karma-games/angular-devkit/src/lib/std/utils";
import { GetRoundHistoryUseCase } from "../round-history/domain/get-round-history.use-case";
import { GetConfigUseCase } from "../core/config/get-config.use-case";
import { SubscribeBalanceUpdateUseCase } from "../core/user/domain/subscribe-balance-update.use-case";
import IOption = SelectComponent.IOption;
import { UpdateBalanceUseCase } from "@karma-games/angular-devkit/src/lib/modules/user";
import { AppTouchService } from "@karma-games/angular-devkit/src/lib/services/app-touch";
import { UpdateRoundInfoViewUseCase } from "@karma-games/angular-devkit/src/lib/services/presenter-cluster";
import { UpdateBalanceViewUseCase } from "@karma-games/angular-devkit/src/lib/services/presenter-cluster";
import { EToasterType } from "@karma-games/angular-devkit/src/lib/modules/toast";
import { BalancePipe } from "@karma-games/angular-devkit/src/lib/modules/pipes";
import { ToastService } from "@karma-games/angular-devkit/src/lib/modules/toast";

@Component({
    selector: "lobby",
    templateUrl: "./lobby.component.html",
    styleUrls: ["./lobby.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: CURRENT_NAMESPACE_TOKEN,
            useFactory: () => {
                return "/game";
            }
        },
        LobbyConnectionStateHandlerService,
        BetPanelsService,
        RoundRepository,
        FetchCurrentRoundStateUseCase,
        GetRoundInitialStateUseCase,
        SendBetUseCase,
        CashOutUseCase,
        CancelBetUseCase,
        SetAutoplayUseCase,
        GetUserBetsUseCase,
        GetUserBetByIndexUseCase,
        GetRoundStateUseCase,
        GetRoundMultiplierUseCase,
        GetRoundUseCase,
        GetRoundIdUseCase,
        GetAutoplayUseCase,
        GetBetButtonStateUseCase,
        ListenForCashOutUseCase,
        ListenForRoundLosedUseCase,
        AddUserBetUseCase,
        RemoveUserBetUseCase,
        GetBetActionInProgressUseCase,
        StopAutoplayUseCase,
        AddRemoveUserRoundBetRecordsUseCase,
        UpdateUserRoundBetRecordUseCase,
        AttachBalanceListenerUseCase,
        SubscribeBalanceUpdateUseCase,
        UpdateBalanceUseCase,
        GetAutoCashOutUseCase,
        GetAutoplaySendBetUseCase
    ]
})
export class LobbyComponent implements OnInit, OnDestroy {
    @ViewChildren(BetPanelWalletAnimationDirective, { read: BetPanelWalletAnimationDirective }) wonAmountElements: QueryList<BetPanelWalletAnimationDirective> = new QueryList<BetPanelWalletAnimationDirective>();
    // @ViewChild("chatEl", { static: true, read: ElementRef }) chatEl: ElementRef = { nativeElement: undefined };
    @ViewChild("gamePanel", { static: true, read: ElementRef }) gamePanel: ElementRef = { nativeElement: undefined };
    @ViewChild("mainContainer", {
        static: true,
        read: ElementRef
    }) mainContainer: ElementRef = { nativeElement: undefined };
    public chatOpened: boolean = false;
    public betPanelData: IBetPanelData = betPanelDataDefault;
    public betButtonStates: Dictionary<IBetButtonState> = {
        0: betButtonStates[EBetButtonState.NoBet],
        1: betButtonStates[EBetButtonState.NoBet]
    };
    public multiBetPanelEnabled: boolean = true;
    public stateFetchInProgress: boolean = true;
    public historyDataArray: number[] = [];
    public balanceAvailable: boolean = false;
    private appOrientation: EScreenOrientation = window.innerWidth < window.innerHeight ? EScreenOrientation.Portrait : EScreenOrientation.Landscape;
    private destroy$: Subject<void> = new Subject<void>();
    private readonly chatAsAbsoluteElBreakPoint: number = 768;

    constructor(private chatHelperService: ChatHelperService,
                private rulerService: RulerService,
                private cdr: ChangeDetectorRef,
                private betPanelsService: BetPanelsService,
                private fetchCurrentRoundStateUseCase: FetchCurrentRoundStateUseCase,
                private connectionStateHandlerService: LobbyConnectionStateHandlerService,
                _roundRepository: RoundRepository,
                _appTouchService: AppTouchService,
                private addRemoveUserRoundBetRecordsUseCase: AddRemoveUserRoundBetRecordsUseCase,
                private updateUserRoundBetRecordUseCase: UpdateUserRoundBetRecordUseCase,
                private currencyService: CurrencyService,
                private listenForCashOutUseCase: ListenForCashOutUseCase,
                private listenForRoundLosedUseCase: ListenForRoundLosedUseCase,
                private attachBalanceListenerUseCase: AttachBalanceListenerUseCase,
                private subscribeBalanceUpdateUseCase: SubscribeBalanceUpdateUseCase,
                private getRoundStateUseCase: GetRoundStateUseCase,
                private updateBalanceUseCase: UpdateBalanceUseCase,
                private getAutoplaySendBetUseCase: GetAutoplaySendBetUseCase,
                private injector: Injector,
                private ngZone: NgZone,
                private getRoundHistoryUseCase: GetRoundHistoryUseCase,
                private getConfigUseCase: GetConfigUseCase,
                private getRoundIdUseCase: GetRoundIdUseCase,
                private updateBalanceViewUseCase: UpdateBalanceViewUseCase,
                private updateRoundInfoViewUseCase: UpdateRoundInfoViewUseCase,
                private balancePipeService: BalancePipe,
                private toaster: ToastService) {
        if (!gsap.plugins["scrollTo"]) {
            gsap.registerPlugin(ScrollToPlugin);
        }
    }

    async ngOnInit(): Promise<void> {
        this.betPanelsService.initializeBetButtonListeners(Object.keys(this.betButtonStates).map(key => Number(key)));
        this.betPanelsService.getBetPanelData().pipe(take(1)).subscribe(data => this.betPanelData = data);

        this.betPanelsService.subscribeBetButtonsStates()
            .pipe(takeUntil(this.destroy$))
            .subscribe((betButtonsStates: Dictionary<IBetButtonState>) => {
                this.betButtonStates = betButtonsStates;
                this.cdr.detectChanges();
            });

        // const initialChatState = {
        //     openedState: false, dimensions: {
        //         width: window.innerWidth,
        //         height: window.innerHeight,
        //         offsetWidth: window.outerWidth,
        //         offsetHeight: window.outerHeight
        //     }
        // };
        // this.setChatOpenedState([initialChatState, initialChatState], window.innerWidth < window.innerHeight ? EScreenOrientation.Portrait : EScreenOrientation.Landscape);

        this.chatHelperService.subscribeChatOpenedState()
            .pipe(startWith(false),
                switchMap((openedState: boolean) => this.rulerService.getAppSize(0)
                    .pipe(map((dimensions: IElementDimensions) => {
                        return { dimensions, openedState };
                    }))),
                pairwise(),
                takeUntil(this.destroy$))
            .subscribe((data: [IChatOpenedStateData, IChatOpenedStateData]) => {
                const prevData: IChatOpenedStateData = data[0];
                const currData: IChatOpenedStateData = data[1];

                const prevOrientation: EScreenOrientation = prevData.dimensions.width < prevData.dimensions.height ? EScreenOrientation.Portrait : EScreenOrientation.Landscape;
                const orientation: EScreenOrientation = currData.dimensions.width < currData.dimensions.height ? EScreenOrientation.Portrait : EScreenOrientation.Landscape;

                const windowDimensionsChanged: boolean = !(prevOrientation === orientation &&
                    ((prevData.dimensions.width <= this.chatAsAbsoluteElBreakPoint && currData.dimensions.width <= this.chatAsAbsoluteElBreakPoint) ||
                        (prevData.dimensions.width > this.chatAsAbsoluteElBreakPoint && currData.dimensions.width > this.chatAsAbsoluteElBreakPoint)));


                if (prevData.openedState === currData.openedState && !windowDimensionsChanged) {
                    return;
                } else if (currData.openedState && windowDimensionsChanged) {
                    this.chatHelperService.toggleChat();
                    return;
                }

                // this.setChatOpenedState(data, orientation);
                this.chatOpened = currData.openedState;
                this.appOrientation = orientation;
                this.cdr.detectChanges();
            });
        const config = await this.getConfigUseCase.execute();
        this.balanceAvailable = config?.balanceAvailable ?? false;
        if (this.balanceAvailable) {
            this.attachBalanceListenerUseCase.execute(this.injector);
            merge(this.connectionStateHandlerService.onReconnect$.pipe(map(() => undefined)),
                this.subscribeBalanceUpdateUseCase.execute())
                .pipe(buffer(this.getRoundStateUseCase.execute()
                        .pipe(filter((state: ERoundState) => state === ERoundState.Idle))),
                    takeUntil(this.destroy$))
                .subscribe((balances: number[]) => {
                    if (balances.length) {
                        const lastBalanceUpdate = balances[balances.length - 1];
                        if (lastBalanceUpdate && typeof lastBalanceUpdate === "number") {
                            this.updateBalanceUseCase.execute(lastBalanceUpdate);
                        }
                    }
                });
        }
        this.getRoundIdUseCase.execute()
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((roundId: string) => {
                this.updateRoundInfoViewUseCase.execute({
                    roundId
                });
            });
        this.addRemoveUserRoundBetRecordsUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        this.listenForRoundLosedUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.onRoundLosed);
        this.listenForCashOutUseCase.execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.onSuccessfulCashOut);
        this.getAutoplaySendBetUseCase.subscribeToAutoplayStopReason()
            .pipe(takeUntil(this.destroy$))
            .subscribe(this.onAutoplayStop);

        try {
            await this.fetchCurrentRoundStateUseCase.execute();
        } catch (e) {
            console.error(e);
        } finally {
            this.stateFetchInProgress = false;
            this.cdr.detectChanges();
        }

        this.getRoundHistoryUseCase.execute()
            .pipe(runInZone(this.ngZone), takeUntil(this.destroy$))
            .subscribe((roundHistoryList: number[]) => {
                this.historyDataArray = roundHistoryList;
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    showSecondBetPanel(): void {
        this.multiBetPanelEnabled = true;
        this.cdr.detectChanges();
    }

    hideSecondBetPanel(): void {
        this.multiBetPanelEnabled = false;
        this.cdr.detectChanges();
    }

    openAutoplayOptionsPopup(template: TemplateRef<any>): void {
        this.betPanelsService.openAutoplayOptionsPopup(template);
    }

    startAutoplay(args: Partial<IAutoplayOptions>): void {
        this.betPanelsService.startAutoplay(args);
    };

    sendBet(args: ISendBetArgs): void {
        this.betPanelsService.sendBet(args);
    };

    cancelBet(betIndex: number): void {
        this.betPanelsService.cancelBet(betIndex);
    };

    removeAutoplay(betIndex: number): void {
        this.betPanelsService.removeAutoplay(betIndex);
    }

    async cashOut(betData: { betIndex: number, split?: EBetSplit }): Promise<void> {
        this.betPanelsService.cashOut(betData.betIndex, betData.split);
    };

    // private setChatOpenedState(data: [IChatOpenedStateData, IChatOpenedStateData], orientation: EScreenOrientation): void {
    //     const prevData: IChatOpenedStateData = data[0];
    //     const currData: IChatOpenedStateData = data[1];
    //     if (orientation === EScreenOrientation.Portrait || currData.dimensions.width <= this.chatAsAbsoluteElBreakPoint) {
    //         gsap.set(this.chatEl.nativeElement, { width: "100%" });
    //         const chatElAnimation = currData.openedState ? {
    //             xPercent: 0,
    //             duration: .5
    //         } : {
    //             xPercent: 100,
    //             duration: .5,
    //             clearProps: "transform"
    //         };
    //         if (prevData.openedState !== currData.openedState) {
    //             gsap.to(this.chatEl.nativeElement, chatElAnimation);
    //         } else {
    //             gsap.set(this.chatEl.nativeElement, chatElAnimation);
    //         }
    //     } else {
    //         const chatElAnimation = currData.openedState ? {
    //             width: "25%"
    //         } : {
    //             width: 0
    //         };
    //         gsap.set(this.chatEl.nativeElement, chatElAnimation);
    //     }
    // }

    private onSuccessfulCashOut = (data: IRoundCashOut) => {
        this.updateUserRoundBetRecordUseCase.execute({
            id: data.betIndex.toString(),
            multiplier: data.multiplierValue,
            cashout: this.currencyService.convertCurrencyAmount(data.amount, data.currency),
            totalAmount: data.totalAmount ? this.currencyService.convertCurrencyAmount(data.totalAmount, data.currency) : undefined,
            split: data.split
        });
        const elIndex: number = this.wonAmountElements.length > 1 ? data.betIndex : 0;
        this.wonAmountElements.toArray()[elIndex]?.startWalletAnimation(data.split);
        // 1s delay because of the wallet animation sync
        timer(1000).subscribe(() => {
            this.updateBalanceViewUseCase.execute({
                amount: data.amount,
                animated: true
            });
        });
    };

    private onRoundLosed = (data: IUserBet[]) => {
        for (let lostRound of data) {
            if (lostRound.roundId) {
                this.updateUserRoundBetRecordUseCase.execute({
                    id: lostRound.betIndex.toString(),
                    date: (Date.now() / 1000),
                    bet: lostRound.amount,
                    multiplier: lostRound.multiplier,
                    cashout: 0
                });
            }
        }
    };

    private onAutoplayStop = (data: IAutoplayStopData): void => {
        switch (data.reason) {
            case EAutoplayStopReason.RoundCount:
                this.toaster.show({
                    type: EToasterType.Warning,
                    translationKey: "AUTOPLAY_STOP.ROUND_COUNT",
                    translationParams: data.data
                });
                break;
            case EAutoplayStopReason.SingleWinAmountExceed:
                this.toaster.show({
                    type: EToasterType.Warning,
                    translationKey: "AUTOPLAY_STOP.SINGLE_EXCEEDED",
                    translationParams: [this.balancePipeService.transform(data.data[0], true, true),
                        this.balancePipeService.transform(data.data[1], true, true)]
                });
                break;
            case EAutoplayStopReason.CashIncrease:
                this.toaster.show({
                    type: EToasterType.Warning,
                    translationKey: "AUTOPLAY_STOP.CASH_INCREASE",
                    translationParams: [this.balancePipeService.transform(data.data[0], true, true)]
                });
                break;
            case EAutoplayStopReason.CashDecrease:
                this.toaster.show({
                    type: EToasterType.Warning,
                    translationKey: "AUTOPLAY_STOP.CASH_DECREASE",
                    translationParams: [this.balancePipeService.transform(data.data[0], true, true)]
                });
                break;
            default:
                break;
        }
    };

    public openAutoCashOutDebugAlert(): void {
        const multiplier = window.prompt("Multiplier");
        const betIndex = window.prompt("Bet index");
        const debugBetAmount = window.prompt("Bet amount");
        this.betPanelsService.setAutoStopDebug(parseFloat(multiplier ?? "0"), parseInt(betIndex ?? "0"),
            parseFloat(debugBetAmount ?? "0"));
    }

    public onStatisticsTabChanged(_tab: IOption): void {
        if (this.appOrientation === EScreenOrientation.Portrait) {
            gsap.to(this.mainContainer.nativeElement, {
                duration: 6,
                scrollTo: { y: this.mainContainer.nativeElement.scrollHeight, autoKill: true },
                ease: "expo.out"
            });
        }
    }

    public historyTrackBy = (_index: number, item: any): string | number => {
        return item;
    };
}
